import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useState } from 'react';
import { Nullable } from 'common-types';
import ButtonGroup from '../../common/components/ButtonGroup';
import { getOneYearFromNow } from '../utils/date-utils';
import { ApiKeyRoleType, CreateApiKeyInput } from '../types/api-key-types';
import { getExpirationTimeSec } from '../utils/api-key-utils';
import ApiKeyDatePicker from './ApiKeyDatePicker';
import ApiKeyNameTextField from './ApiKeyNameTextField';
import ApiKeySecretTextField from './ApiKeySecretTextField';
import ApiKeyTypeRadioButton from './ApiKeyTypeRadioButton';

const CreateApiKeyForm: FC<
  React.PropsWithChildren<CreateApiKeyFormProps>
> = props => {
  const { onCreateApiKey, onClose, apiKeyRoleTypes, isLoading, keySecret } =
    props;
  const { classes } = useStyles();

  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyType, setApiKeyType] = useState<ApiKeyRoleType>(
    ApiKeyRoleType.CICDIntegration,
  );
  const [date, setDate] = useState<Dayjs | null>(dayjs(getOneYearFromNow()));

  const handleCreate = useCallback(() => {
    if (!date) {
      return;
    }

    const createApiKeyInput: CreateApiKeyInput = {
      name: apiKeyName,
      apiKeyRoleType: apiKeyType,
      description: '',
      expiresInSec: getExpirationTimeSec(date),
    };
    onCreateApiKey(createApiKeyInput);
  }, [onCreateApiKey, apiKeyName, date, apiKeyType]);

  return (
    <>
      <form className={classes.rootForm}>
        <Box display='flex' flexDirection='column' gap={3} mt={1} mb={6}>
          <ApiKeyNameTextField
            apiKeyName={apiKeyName}
            setApiKeyName={setApiKeyName}
          />
          <ApiKeyTypeRadioButton
            apiKeyRoleTypes={apiKeyRoleTypes}
            apiKeyType={apiKeyType}
            setApiKeyType={setApiKeyType}
          />
          <ApiKeyDatePicker
            title={'Expiration Date'}
            date={date}
            setDate={setDate}
          />
          <Box>
            <LoadingButton
              tabIndex={1}
              loading={isLoading}
              size='medium'
              variant='contained'
              color='primary'
              disabled={!apiKeyName || !date}
              onClick={handleCreate}>
              Create
            </LoadingButton>
          </Box>
          {keySecret && <ApiKeySecretTextField keySecret={keySecret} />}
        </Box>
      </form>
      <ButtonGroup
        onCancel={onClose}
        onClick={onClose}
        isLoading={false}
        buttonCancelLabel='Cancel'
        buttonCancelTooltip='Cancel this operation'
        buttonActionLabel='Done'
        buttonActionTooltip='Close this modal'
        buttonActionColor='primary'
        isActionDisabled={false}
      />
    </>
  );
};

const useStyles = makeStyles()(() => ({
  rootForm: {
    width: '100%',
  },
}));

export interface CreateApiKeyFormProps {
  onCreateApiKey: (createApiKeyInput: CreateApiKeyInput) => void;
  onClose: () => void;
  apiKeyRoleTypes: ApiKeyRoleType[];
  isLoading: boolean;
  errorMessage: Nullable<string>;
  keySecret: Nullable<string>;
}

export default CreateApiKeyForm;
